import { useState, useEffect, useRef, useCallback } from 'react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import type { QltyDashboardIssuesResult } from '../qlty.api.types';

export interface QltyIssueFilters {
  level?: string;
  category?: string;
  tool?: string;
  mode?: string;
  search?: string;
}

const PAGE_SIZE = 50;

export function useQltyIssues(entityRef: string, filters: QltyIssueFilters) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<QltyDashboardIssuesResult>({
    total: 0,
    items: [],
    page: 1,
    pageSize: PAGE_SIZE,
    hasNextPage: false,
    facets: { categories: [], tools: [], levels: [], modes: [] },
  });
  const [page, setPage] = useState(1);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Reset to page 1 when filters change
  const filtersKey = `${filters.level}|${filters.category}|${filters.tool}|${filters.mode}|${filters.search}`;
  const prevFiltersKey = useRef(filtersKey);
  useEffect(() => {
    if (prevFiltersKey.current !== filtersKey) {
      prevFiltersKey.current = filtersKey;
      setPage(1);
    }
  }, [filtersKey]);

  const load = useCallback(async (currentPage: number, abortSignal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('qlty');
      const encoded = encodeURIComponent(entityRef);
      const params = new URLSearchParams();
      if (filters.level) params.set('level', filters.level);
      if (filters.category) params.set('category', filters.category);
      if (filters.tool) params.set('tool', filters.tool);
      if (filters.mode) params.set('mode', filters.mode);
      if (filters.search) params.set('search', filters.search);
      params.set('page', String(currentPage));
      params.set('limit', String(PAGE_SIZE));
      const qs = params.toString();
      const url = `${baseUrl}/${encoded}/dashboard/issues?${qs}`;
      const res = await fetchApi.fetch(url);
      if (abortSignal?.aborted) return;
      if (res.ok) {
        const result: QltyDashboardIssuesResult = await res.json();
        setData(prev =>
          currentPage === 1
            ? result
            : { ...result, items: [...prev.items, ...result.items] },
        );
      } else {
        setError(`Failed to load issues (${res.status})`);
      }
    } catch (err) {
      if (abortSignal?.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to load issues');
    } finally {
      if (!abortSignal?.aborted) setLoading(false);
    }
  }, [entityRef, discoveryApi, fetchApi, filters.level, filters.category, filters.tool, filters.mode, filters.search]);

  useEffect(() => {
    const abortController = new AbortController();

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      load(page, abortController.signal);
    }, filters.search ? 300 : 0);

    return () => {
      abortController.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [load, page, filters.search]);

  const loadNextPage = useCallback(() => {
    if (!loading && data.hasNextPage) {
      setPage(prev => prev + 1);
    }
  }, [loading, data.hasNextPage]);

  return { loading, error, data, hasNextPage: data.hasNextPage, loadNextPage };
}
